/**
 * @copyright WaterStreet. All rights reserved.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

import {
	ChangeDetectorRef,
	Component,
	Input,
	OnInit,
	OnChanges,
	SimpleChanges
} from '@angular/core';
import {
	DomSanitizer,
	SafeResourceUrl
} from '@angular/platform-browser';
import {
	FieldType
} from '@ngx-formly/core';
import { AppConstants } from '@shared/constants/app.constants';
import {
	AnyHelper
} from '@shared/helpers/any.helper';
import {
	StringHelper
} from '@shared/helpers/string.helper';

@Component({
	selector: 'custom-image-display',
	templateUrl: './custom-image-display.component.html',
	styleUrls: ['./custom-image-display.component.scss']
})

/**
 * A component representing an instance of a Custom Image Display.
 * This is a read-only image display component that supports loading
 * a Base64 image from a data promise, a direct value, or the field model.
 * https://ngx-formly.github.io/ngx-formly/guide
 *
 * @export
 * @class CustomImageDisplayComponent
 * @extends {FieldType}
 * @implements {OnInit}
 */
export class CustomImageDisplayComponent
	extends FieldType
	implements OnInit, OnChanges
{
	/**
	 * Creates an instance of a CustomImageDisplayComponent.
	 *
	 * @param {DomSanitizer} sanitizer
	 * The sanitizer that helps to avoid XSS bugs.
	 * @param {ChangeDetectorRef} changeDetector
	 * The change detector reference for this component.
	 * @memberof CustomImageDisplayComponent
	 */
	public constructor(
		private readonly sanitizer: DomSanitizer,
		private readonly changeDetector: ChangeDetectorRef)
	{
		super();
	}

	/**
	 * Gets or sets the field execution context when used as a standalone
	 * component (needed to correctly bind `this` inside the dataPromise
	 * function, e.g. to access `this.source.resolver`). Falls back to
	 * `field.props.context` when not set.
	 *
	 * @type {any}
	 * @memberof CustomImageDisplayComponent
	 */
	@Input()
	public context: any;

	/**
	 * Gets or sets the direct Base64 (or data url) value when used as a
	 * standalone component. Falls back to `field.props.value` when not set.
	 *
	 * @type {string}
	 * @memberof CustomImageDisplayComponent
	 */
	@Input() public value: string;

	/**
	 * Gets or sets the data promise string when used as a standalone
	 * component. Falls back to `field.props.dataPromise` when not set.
	 *
	 * @type {string}
	 * @memberof CustomImageDisplayComponent
	 */
	@Input() public dataPromise: string;

	/**
	 * Gets or sets the alt text when used as a standalone component.
	 * Falls back to `field.props.altText` when not set.
	 *
	 * @type {string}
	 * @memberof CustomImageDisplayComponent
	 */
	@Input() public altText: string;

	/**
	 * Gets or sets the description when used as a standalone component.
	 * Falls back to `field.props.description` when not set.
	 *
	 * @type {string}
	 * @memberof CustomImageDisplayComponent
	 */
	@Input() public description: string;

	/**
	 * Gets or sets whether the image is clickable when used as a
	 * standalone component. Falls back to `field.props.clickable`.
	 *
	 * @type {boolean}
	 * @memberof CustomImageDisplayComponent
	 */
	@Input() public isClickable: boolean;

	/**
	 * Gets or sets whether the image renders visually disabled when used
	 * as a standalone component. Falls back to
	 * `field.props.visuallyDisabled`.
	 *
	 * @type {boolean}
	 * @memberof CustomImageDisplayComponent
	 */
	@Input() public isVisuallyDisabled: boolean;

	/**
	 * Gets or sets whether the component should render in compact/icon
	 * mode, omitting the label, description, and "no image" fallback
	 * message. Used when the component is embedded inline next to a
	 * tooltip icon rather than displayed as a standalone image field.
	 * Always set explicitly by the consuming template — never sourced
	 * from `field.props`.
	 *
	 * @type {boolean}
	 * @memberof CustomImageDisplayComponent
	 */
	@Input() public iconMode: boolean = false;

	/**
	 * Gets or sets the resolved safe image source url.
	 *
	 * @type {SafeResourceUrl}
	 * @memberof CustomImageDisplayComponent
	 */
	public imageSource: SafeResourceUrl;

	/**
	 * Gets or sets the loading state of the component.
	 *
	 * @type {boolean}
	 * @memberof CustomImageDisplayComponent
	 */
	public loading: boolean = true;

	/**
	 * Gets whether the image has a valid source to display.
	 *
	 * @readonly
	 * @type {boolean}
	 * @memberof CustomImageDisplayComponent
	 */
	public hasImage: boolean = false;

	/**
	 * Gets the resolved label to display, prioritizing the standalone
	 * label over the field prop label.
	 * Note: label is intentionally omitted when in icon mode, so no
	 * fallback resolution is needed for that case here.
	 *
	 * @readonly
	 * @type {string}
	 * @memberof CustomImageDisplayComponent
	 */
	public resolvedLabel: string = AppConstants.empty;

	/**
	 * Gets the resolved description, prioritizing the standalone
	 * @Input() over `field.props.description`.
	 *
	 * @readonly
	 * @type {string}
	 * @memberof CustomImageDisplayComponent
	 */
	public resolvedDescription: string = AppConstants.empty;

	/**
	 * Gets the resolved alt text, prioritizing the standalone
	 * @Input() over `field.props.altText`.
	 *
	 * @readonly
	 * @type {string}
	 * @memberof CustomImageDisplayComponent
	 */
	public resolvedAltText: string = AppConstants.empty;

	/**
	 * Gets whether the image is clickable, prioritizing the standalone
	 * @Input() over `field.props.clickable`.
	 *
	 * @readonly
	 * @type {boolean}
	 * @memberof CustomImageDisplayComponent
	 */
	public resolvedIsClickable: boolean = false;

	/**
	 * Gets whether the image should render visually disabled, prioritizing
	 * the standalone @Input() over `field.props.visuallyDisabled`.
	 *
	 * @readonly
	 * @type {boolean}
	 * @memberof CustomImageDisplayComponent
	 */
	public resolvedIsVisuallyDisabled: boolean = false;

	/**
	 * On initialization event.
	 * Resolves the image source from the dataPromise, value prop, or
	 * falls back to an empty state.
	 *
	 * @async
	 * @memberof CustomImageDisplayComponent
	 */
	public ngOnInit(): void
	{
		this.updateResolvedProperties();
	}

	/**
	 * On changes event.
	 *
	 * @async
	 * @memberof CustomImageDisplayComponent
	 */
	public ngOnChanges(
		changes: SimpleChanges): void
	{
		if (changes.field)
		{
			this.updateResolvedProperties();
		}
	}

	/**
	 * Resolves the image source to display. The priority order is:
	 * 1. dataPromise @Input() (standalone usage)
	 * 2. field.props.dataPromise (Formly field type usage)
	 * 3. value @Input() (standalone usage)
	 * 4. field.props.value (Formly field type usage)
	 *
	 * @async
	 * @memberof CustomImageDisplayComponent
	 */
	public async resolveImageSource(): Promise<void>
	{
		let base64String: string = null;

		const resolvedDataPromise: string =
			this.dataPromise ?? this.field?.props?.dataPromise;
		const resolvedContext: any =
			this.context ?? this.field?.props?.context;

		if (!AnyHelper.isNullOrWhitespace(resolvedDataPromise))
		{
			base64String =
				await StringHelper.transformToFieldDataPromise(
					resolvedDataPromise,
					resolvedContext,
					this.field);
		}
		else
		{
			const resolvedValue: string =
				this.value ?? this.field?.props?.value;

			if (!AnyHelper.isNullOrWhitespace(resolvedValue))
			{
				base64String = resolvedValue;
			}
		}

		if (AnyHelper.isNullOrWhitespace(base64String))
		{
			this.imageSource = null;

			return;
		}

		const trimmedValue: string =
			base64String.trim();

		if (trimmedValue.startsWith('data:')
			&& !trimmedValue.startsWith('data:image/'))
		{
			this.imageSource = null;

			return;
		}

		const resolvedMimeType: string =
			(this.field?.props?.mimeType ?? 'image/png').toLowerCase();

		if (!resolvedMimeType.startsWith('image/'))
		{
			this.imageSource = null;

			return;
		}

		const dataUrl: string =
			trimmedValue.startsWith('data:')
				? trimmedValue
				: `data:${resolvedMimeType};base64,${trimmedValue}`;
		// dataPromise, mimeType, and value are always internal
		// configurations defined by developers on the team — there is
		// no end-user input or external source in this flow, so
		// bypassing sanitization here is safe.
		this.imageSource =
			this.sanitizer.bypassSecurityTrustResourceUrl(dataUrl);
	}

	/**
	 * Updates the resolved properties of the component based on the current
	 * field and input values. This includes resolving the image source,
	 * label, description, alt text, clickable state, and visually disabled
	 * state.
	 *
	 * @async
	 * @memberof CustomImageDisplayComponent
	 */
	private updateResolvedProperties(): void
	{
		this.resolveImageSource().then(() => {
			this.hasImage =
				!AnyHelper.isNull(this.imageSource);
			this.resolvedLabel =
				this.field?.props?.label;
			this.resolvedDescription =
				this.description ?? this.field?.props?.description;
			this.resolvedAltText =
				this.altText
					?? this.field?.props?.altText
					?? 'Displayed Image';
			this.resolvedIsClickable =
				 this.isClickable === true
				 	|| this.field?.props?.clickable === true;
			this.resolvedIsVisuallyDisabled =
				this.isVisuallyDisabled === true
					|| this.field?.props?.visuallyDisabled === true;
			this.loading = false;
			this.changeDetector.detectChanges();
		});
	}
}